const TOKEN_KEY = 'luma_session_token';
const ADMIN_TOKEN_KEY = 'zaomeng_admin_session_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || '';
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

function newIdempotencyKey(prefix = 'generation') {
  const id = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${id}`;
}

export async function request(path, options = {}) {
  const { authScope = '', ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers || {});
  const token = authScope === 'admin' || path.startsWith('/api/admin/')
    ? getAdminToken()
    : getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const body = fetchOptions.body;
  if (body && !(body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let response;
  try {
    response = await fetch(path, { ...fetchOptions, cache: fetchOptions.cache || 'no-store', headers });
  } catch (cause) {
    const error = new Error('无法连接本地服务，请确认 4173 服务正在运行后重试。');
    error.cause = cause;
    error.code = 'local_service_unreachable';
    throw error;
  }
  const type = response.headers.get('content-type') || '';
  const payload = type.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const error = new Error(payload?.message || payload?.error || `请求失败 (${response.status})`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

async function requestAllPages(path, key) {
  const limit = 200;
  const rows = [];
  let page = 1;
  let pages = 1;
  let firstResult;

  do {
    const separator = path.includes('?') ? '&' : '?';
    const result = await request(`${path}${separator}page=${page}&limit=${limit}`);
    firstResult ||= result;
    rows.push(...(result[key] || result.data || []));
    pages = Math.max(1, Number(result.pagination?.pages || 1));
    page += 1;
  } while (page <= pages);

  return {
    ...firstResult,
    [key]: rows,
    data: rows,
    pagination: {
      ...(firstResult?.pagination || {}),
      page: 1,
      limit,
      total: rows.length,
      pages,
    },
  };
}

export const api = {
  bootstrap: () => request('/api/bootstrap'),
  storage: () => request('/api/storage'),
  publicConfig: () => request('/api/public/config').then((result) => result.config || result),
  canvasCapabilities: (refresh = false) => request(`/api/canvas/capabilities${refresh ? '?refresh=true' : ''}`).then((result) => result.capabilities || result),
  adminCapabilities: (refresh = false) => request('/api/canvas/capabilities' + (refresh ? '?refresh=true' : ''), { authScope: 'admin' }).then((result) => result.capabilities || result),
  syncMioraModels: () => request('/api/admin/miora/sync', { method: 'POST', authScope: 'admin' }),
  login: (credentials) => request('/api/auth/login', { method: 'POST', body: JSON.stringify(credentials) }),
  adminLogin: (credentials) => request('/api/admin/login', { method: 'POST', body: JSON.stringify(credentials) }),
  register: (details) => request('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(details)
  }),
  logout: () => request('/api/auth/logout', { method: 'POST' }).finally(() => setToken('')),
  adminLogout: () => request('/api/admin/logout', { method: 'POST', authScope: 'admin' }).finally(() => setAdminToken('')),
  generate: ({
    prompt, model, ratio, resolution, count, stylePreset,
    references = [], projectId = '', canvasNodeId = '', idempotencyKey = ''
  }) => {
    const form = new FormData();
    form.append('payload', JSON.stringify({
      mode: 'image', prompt, model, ratio, resolution, count, style: stylePreset, presetId: stylePreset,
      projectId, canvasNodeId
    }));
    references.forEach((item) => form.append('references', item.file, item.file.name));
    return request('/api/tasks', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey('image') },
      body: form,
    });
  },
  generateVideo: ({ prompt, model, ratio, duration, resolution = '', generationMode = '', references = [], projectId = '', canvasNodeId = '', idempotencyKey = '' }) => {
    const form = new FormData();
    form.append('payload', JSON.stringify({ prompt, model, ratio, duration, resolution, generationMode, projectId, canvasNodeId }));
    references.forEach((item, index) => {
      form.append('references', item.file, item.file.name || `图片${index + 1}`);
      form.append('reference_indexes', String(index + 1));
    });
    return request('/api/video-tasks', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey('video') },
      body: form,
    });
  },
  videoTasks: () => request('/api/video-tasks'),
  videoTask: (id) => request(`/api/video-tasks/${encodeURIComponent(id)}`),
  deleteVideoTask: (id) => request(`/api/video-tasks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  cancelVideoTask: (id) => request(`/api/video-tasks/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  paymentPackages: () => request('/api/payments/packages'),
  paymentOrders: () => request('/api/payments/orders'),
  paymentOrder: (id) => request(`/api/payments/orders/${encodeURIComponent(id)}`),
  createPaymentOrder: (packageId, type, idempotencyKey = '') => request('/api/payments/orders', {
    method: 'POST',
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    body: JSON.stringify({ packageId, type }),
  }),
  deleteTask: (id) => request(`/api/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  deleteTaskImage: (id, index) => request(`/api/tasks/${encodeURIComponent(id)}/images/${Number(index)}`, { method: 'DELETE' }),
  setTaskGroup: (id, groupName) => request(`/api/tasks/${encodeURIComponent(id)}/group`, {
    method: 'PATCH',
    body: JSON.stringify({ groupName })
  }),
  projects: () => request('/api/projects'),
  canvas: (projectId) => request(`/api/projects/${encodeURIComponent(projectId)}/canvas`),
  saveCanvas: (projectId, state) => request(`/api/projects/${encodeURIComponent(projectId)}/canvas`, {
    method: 'PUT',
    body: JSON.stringify(state)
  }),
  uploadCanvasAsset: ({ projectId, file, name, width = 0, height = 0, duration = 0 }) => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('projectId', projectId);
    form.append('name', name || file.name);
    form.append('width', String(width || 0));
    form.append('height', String(height || 0));
    form.append('duration', String(duration || 0));
    return request('/api/canvas-assets', { method: 'POST', body: form });
  },
  renameCanvasAsset: (id, name) => request(`/api/canvas-assets/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify({ name })
  }),
  deleteCanvasAsset: (id) => request(`/api/canvas-assets/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  createProject: (name) => request('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  updateProject: (id, body) => request(`/api/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  }),
  copyProject: (id, name = '') => request(`/api/projects/${encodeURIComponent(id)}/copy`, {
    method: 'POST', body: JSON.stringify({ name })
  }),
  createMaterialCollection: (projectId, name) => request(`/api/projects/${encodeURIComponent(projectId)}/material-collections`, {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteMaterialCollection: (projectId, collectionId) => request(`/api/projects/${encodeURIComponent(projectId)}/material-collections/${encodeURIComponent(collectionId)}`, {
    method: 'DELETE'
  }),
  deleteProject: (id, options = {}) => {
    const search = new URLSearchParams();
    if (options.strategy) search.set('strategy', options.strategy);
    if (options.targetProjectId) search.set('targetProjectId', options.targetProjectId);
    const query = search.size ? `?${search.toString()}` : '';
    return request(`/api/projects/${encodeURIComponent(id)}${query}`, { method: 'DELETE' });
  },
  materials: ({ projectId = '', category = '', q = '' } = {}) => {
    const search = new URLSearchParams();
    if (projectId) search.set('projectId', projectId);
    if (category) search.set('category', category);
    if (q) search.set('q', q);
    const query = search.size ? `?${search.toString()}` : '';
    return request(`/api/materials${query}`);
  },
  createMaterial: (input) => {
    if (!input?.file) {
      return request('/api/materials', { method: 'POST', body: JSON.stringify(input || {}) });
    }

    const form = new FormData();
    const payload = {
      projectId: input.projectId,
      category: input.category,
      mediaType: input.mediaType || input.kind || '',
      name: input.name,
      description: input.description || '',
      source: input.source || 'import'
    };
    form.append('file', input.file, input.file.name);
    form.append('payload', JSON.stringify(payload));
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) form.append(key, String(value));
    });
    return request('/api/materials', { method: 'POST', body: form });
  },
  updateMaterial: (id, body) => request(`/api/materials/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  }),
  deleteMaterial: (id) => request(`/api/materials/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  setMaterialCover: (id) => request(`/api/materials/${encodeURIComponent(id)}/cover`, { method: 'POST' }),
  task: (id) => request(`/api/tasks/${encodeURIComponent(id)}`),
  updateProfile: (body) => request('/api/me/password', {
    method: 'PATCH',
    body: JSON.stringify({ currentPassword: body.oldPassword, newPassword: body.newPassword })
  }),
  admin: {
    storage: () => request('/api/admin/storage'),
    saveStorageSettings: (body) => request('/api/admin/storage/settings', { method: 'PUT', body: JSON.stringify(body) }),
    setUserStorageQuota: (id, quotaBytes, clear = false) => request(`/api/admin/users/${encodeURIComponent(id)}/storage`, {
      method: 'PUT',
      body: JSON.stringify({ quotaBytes, clear }),
    }),
    providers: () => request('/api/admin/providers'),
    createProvider: (body) => request('/api/admin/providers', { method: 'POST', body: JSON.stringify(body) }),
    updateProvider: (id, body) => request(`/api/admin/providers/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteProvider: (id) => request(`/api/admin/providers/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    setProviderCredential: (id, apiKey) => request(`/api/admin/providers/${encodeURIComponent(id)}/credential`, { method: 'POST', body: JSON.stringify({ apiKey }) }),
    clearProviderCredential: (id) => request(`/api/admin/providers/${encodeURIComponent(id)}/credential`, { method: 'DELETE' }),
    testProvider: (id) => request(`/api/admin/providers/${encodeURIComponent(id)}/test`, { method: 'POST' }),
    syncProviderModels: (id) => request(`/api/admin/providers/${encodeURIComponent(id)}/sync-models`, { method: 'POST' }),
    providerModels: (id) => request(`/api/admin/providers/${encodeURIComponent(id)}/models`),
    updateProviderModel: (providerId, modelId, body) => request(`/api/admin/providers/${encodeURIComponent(providerId)}/models/${encodeURIComponent(modelId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    overview: () => request('/api/admin/overview').then((result) => {
      const data = result.data || result;
      return {
        users: data.users,
        points: data.totalUserPoints ?? data.pointsConsumed,
        todayTasks: data.todayTasks ?? data.tasks,
        completedTasks: data.completedTasks ?? 0
      };
    }),
    users: () => requestAllPages('/api/admin/users', 'users').then((result) => ({
      ...result,
      users: (result.users || []).map((user) => ({
        ...user,
        status: user.banned ? 'banned' : 'active',
        createdAt: new Date(user.createdAt).toLocaleString('zh-CN', { hour12: false })
      }))
    })),
    adjustPoints: (id, delta, note) => request(`/api/admin/users/${id}/points`, {
      method: 'POST', body: JSON.stringify({ delta, reason: note })
    }),
    setStatus: (id, status) => request(`/api/admin/users/${id}`, {
      method: 'PATCH', body: JSON.stringify({ banned: status === 'banned' })
    }),
    resetPassword: (id, password) => request(`/api/admin/users/${id}`, {
      method: 'PATCH', body: JSON.stringify({ password })
    }),
    config: () => request('/api/admin/config').then((result) => {
      const config = result.config || result;
      return {
        ...config,
        models: (config.models || []).map((model) => ({
          ...model,
          cost: Number(model.costPerImage || 0),
          costByResolution: Object.fromEntries((model.resolutions || ['1K', '2K', '4K']).map((resolution) => [
            resolution,
            Number(model.costByResolution?.[resolution] ?? model.costPerImage ?? 0),
          ])),
        })),
        rechargeUrl: config.links?.rechargeUrl || '',
        adminContact: config.links?.adminContact || '',
        adminQrUrl: config.links?.adminQrUrl || '',
        brandColor: config.brand?.primaryColor || '#78a9ff',
        generation: config.generation || {},
        video: config.video || {},
        modelDisplay: config.modelDisplay || {},
      };
    }),
    saveConfig: (body) => {
      const payload = {};
      if (body.brand) payload.brand = { ...body.brand, ...(body.brandColor === undefined ? {} : { primaryColor: body.brandColor }) };
      if (body.registration) payload.registration = { enabled: body.registration.enabled !== false };
      if (body.links || body.rechargeUrl !== undefined || body.adminContact !== undefined || body.adminQrUrl !== undefined) {
        payload.links = {
          ...(body.links || {}),
          ...(body.rechargeUrl === undefined ? {} : { rechargeUrl: body.rechargeUrl }),
          ...(body.adminContact === undefined ? {} : { adminContact: body.adminContact }),
          ...(body.adminQrUrl === undefined ? {} : { adminQrUrl: body.adminQrUrl }),
        };
      }
      if (body.generation) payload.generation = body.generation;
      if (body.video) payload.video = body.video;
      if (body.modelDisplay) payload.modelDisplay = body.modelDisplay;
      if (Array.isArray(body.models)) {
        payload.models = body.models.map((model) => ({
          ...model,
          costPerImage: Number(model.cost),
          costByResolution: Object.fromEntries(Object.entries(model.costByResolution || {}).map(([resolution, cost]) => [resolution, Number(cost)])),
        }));
      }
      return request('/api/admin/config', { method: 'PUT', body: JSON.stringify(payload) });
    },
    logs: (kind) => {
      const path = kind === 'points'
        ? '/api/admin/logs/points?limit=200'
        : '/api/admin/logs/generations?limit=200';
      return request(path).then((result) => ({
      logs: (result.logs || []).map((item) => kind === 'points' ? {
        ...item,
        balance: item.balanceAfter,
        createdAt: new Date(item.createdAt).toLocaleString('zh-CN', { hour12: false })
      } : {
        ...item,
        model: item.modelId,
        ratio: item.parameters?.ratio,
        resolution: item.parameters?.resolution,
        count: item.parameters?.count,
        createdAt: new Date(item.createdAt).toLocaleString('zh-CN', { hour12: false })
      })
      }));
    },
    generationLogs: (query = {}) => {
      const search = new URLSearchParams(query);
      return request(`/api/admin/logs/generations${search.size ? `?${search.toString()}` : ''}`).then((result) => ({
        ...result,
        logs: (result.logs || []).map((item) => ({
          ...item,
          model: item.modelId || item.model,
          ratio: item.ratio || item.parameters?.ratio,
          resolution: item.resolution || item.parameters?.resolution,
          count: item.count || item.parameters?.count,
          createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString('zh-CN', { hour12: false }) : '-',
        })),
      }));
    },
    paymentLogs: (query = {}) => {
      const search = new URLSearchParams(query);
      return request(`/api/admin/logs/payments${search.size ? `?${search.toString()}` : ''}`);
    },
    deletePaymentLogs: ({ ids = [], filters = {}, clear = false } = {}) => request('/api/admin/logs/payments', {
      method: 'DELETE',
      body: JSON.stringify({ ids, filters: { ...filters, clear } }),
    }),
    paymentSettings: () => request('/api/admin/payment-settings'),
    savePaymentSettings: (body) => request('/api/admin/payment-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
    deleteGenerationLogs: ({ ids = [], filters = {}, clear = false } = {}) => request('/api/admin/logs/generations', {
      method: 'DELETE',
      body: JSON.stringify({ ids, filters, clear }),
    }),
    tasks: (query = {}) => {
      const search = new URLSearchParams(query);
      return requestAllPages(`/api/admin/tasks${search.size ? `?${search.toString()}` : ''}`, 'tasks');
    },
    exportLogs: (kind) => {
      const resource = kind === 'points' ? 'points' : 'generations';
      return request(`/api/admin/logs/${resource}?format=csv`);
    },
    reviewTasks: () => requestAllPages('/api/admin/tasks/review', 'tasks').then((result) => ({
      ...result,
      tasks: (result.tasks || []).map((task) => ({
        ...task,
        createdAt: new Date(task.createdAt).toLocaleString('zh-CN', { hour12: false }),
        updatedAt: new Date(task.updatedAt).toLocaleString('zh-CN', { hour12: false })
      }))
    })),
    resumeReviewTask: (id) => request(`/api/admin/tasks/${encodeURIComponent(id)}/resume`, { method: 'POST' }),
    refundReviewTask: (id) => request(`/api/admin/tasks/${encodeURIComponent(id)}/refund`, { method: 'POST' }),
    uploadConfigMedia: (slot, file) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return request(`/api/admin/config/media/${encodeURIComponent(slot)}`, { method: 'POST', body: form });
    },
    deleteConfigMedia: (slot) => request(`/api/admin/config/media/${encodeURIComponent(slot)}`, { method: 'DELETE' }),
  }
};
